import { useTranslation } from '../../../shared/i18n';
import { formatMoney, currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Statistic, Table, Tag, Tooltip, message,
} from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';

// VPC peering + cross-AZ/region traffic tabs of a VPC (MyVpc expanded row).

const call = async (method, url, data) => {
    try {
        const res = method === 'get' ? await apiClient.get(url, { params: data }) : await apiClient.post(url, data);
        return res.data || {};
    } catch (e) {
        return { status: 'error', msg: e.response?.data?.msg || e.message };
    }
};

const fmtBytes = (b) => {
    const n = Number(b) || 0;
    if (n >= 1024 ** 4) return `${(n / 1024 ** 4).toFixed(2)} TB`;
    if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
    return `${(n / 1024 ** 2).toFixed(1)} MB`;
};

const useClassLabel = () => {
    const { t } = useTranslation();
    return (c) => ({ same_az: t('Same zone'), cross_az: t('Cross-zone'), cross_region: t('Cross-region') }[c] || c);
};

export const VpcPeeringTab = ({ vpc, onChanged }) => {
    const { t } = useTranslation();
    const classLabel = useClassLabel();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        const res = await call('get', '/api/user/evm/vpc-peering', { vpc_id: vpc.id });
        setRows(res.status === 'success' ? (res.data?.peerings || []) : []);
        setLoading(false);
    }, [vpc.id]);
    useEffect(() => { load(); }, [load]);

    const act = async (id, op) => {
        const res = await call('post', `/api/user/evm/vpc-peering/${id}/${op}`);
        if (res.status === 'success') {
            message.success(t('Completed'));
            load();
            onChanged?.();
        } else {
            message.error(res.msg || t('Operation failed'));
        }
    };

    const create = async (v) => {
        setSaving(true);
        const res = await call('post', '/api/user/evm/vpc-peering', { vpc_id: vpc.id, ...v });
        setSaving(false);
        if (res.status === 'success') {
            message.success(t('Request sent'));
            setOpen(false);
            load();
        } else {
            message.error(res.msg || t('Creation failed'));
        }
    };

    const statusTag = (s) => {
        const m = { pending: ['orange', t('Pending acceptance')], active: ['green', t('Connected')], rejected: ['red', t('Rejected')], expired: ['default', t('Expired')], deleted: ['default', t('Deleted')] };
        const [c, txt] = m[s] || ['default', s];
        return <Tag color={c}>{txt}</Tag>;
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        {
            title: t('Peer'),
            render: (_, r) => {
                const peer = r.requester.vpc_id === vpc.id ? r.accepter : r.requester;
                return (
                    <Space size={4} wrap>
                        <span>VPC {peer.vpc_id}</span>
                        <Tag>{peer.cidr}</Tag>
                        {peer.account && <span style={{ opacity: 0.7 }}>{peer.account}</span>}
                    </Space>
                );
            },
        },
        { title: t('Direction'), render: (_, r) => (r.requester.vpc_id === vpc.id ? t('Sent') : t('Received')), width: 70 },
        { title: t('Status'), dataIndex: 'status', render: statusTag, width: 90 },
        {
            title: t('Traffic'),
            width: 150,
            render: (_, r) => (r.cross && r.cross !== 'same_az'
                ? <Tooltip title={t('Billed to the sender')}>{classLabel(r.cross)} {formatMoney(r.price_per_gb, r.currency || PLATFORM_CURRENCY)}/GB</Tooltip>
                : t('Free')),
        },
        {
            title: '',
            width: 110,
            render: (_, r) => {
                const incoming = r.status === 'pending' && r.accepter.vpc_id === vpc.id && r.role === 'accepter';
                return (
                    <Space size={4}>
                        {incoming && <Tooltip title={t('Accept')}><Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => act(r.id, 'accept')} /></Tooltip>}
                        {incoming && <Tooltip title={t('Deny')}><Button size="small" icon={<CloseOutlined />} onClick={() => act(r.id, 'reject')} /></Tooltip>}
                        {(r.status === 'active' || (r.status === 'pending' && !incoming)) && (
                            <Popconfirm title={r.status === 'active' ? t('Both sides will disconnect immediately after deletion. Continue?') : t('Withdraw the request?')} onConfirm={() => act(r.id, 'delete')}>
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 8 }}>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>{t('New peering')}</Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table size="small" rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={false}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No peering connections')} /> }} />
            <Modal title={t('New peering')} open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} confirmLoading={saving} destroyOnClose>
                <Form form={form} layout="vertical" onFinish={create}>
                    <Form.Item name="peer_vpc_id" label={t('Peer VPC ID')} rules={[{ required: true, message: t('Required') }]}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="peer_account" label={t('Peer account')} tooltip={t('Username or email')} rules={[{ required: true, message: t('Required') }]}>
                        <Input maxLength={100} />
                    </Form.Item>
                    <Form.Item name="name" label={t('Name')} style={{ marginBottom: 0 }}>
                        <Input maxLength={100} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export const VpcTrafficTab = ({ vpc }) => {
    const { t } = useTranslation();
    const classLabel = useClassLabel();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [pairs, setPairs] = useState(null); // zone/area pairs where the VPC's (or peer's) VMs are
    const [peerings, setPeerings] = useState([]);
    const [quote, setQuote] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const loadPairs = async (peeringId) => {
        setPairs(null);
        const r = await call('get', '/api/user/evm/vpc-bw/pairs', { vpc_id: vpc.id, peering_id: peeringId || 0 });
        setPairs(r.status === 'success' ? r.data?.pairs || [] : []);
    };

    const load = useCallback(async () => {
        const res = await call('get', `/api/user/evm/vpc/${vpc.id}/traffic`);
        setData(res.status === 'success' ? res.data : null);
    }, [vpc.id]);
    useEffect(() => { load(); }, [load]);

    const openBuy = async () => {
        form.resetFields();
        setQuote(null);
        setOpen(true);
        loadPairs(0);
        const p = await call('get', '/api/user/evm/vpc-peering', { vpc_id: vpc.id });
        setPeerings((p.data?.peerings || []).filter((x) => x.status === 'active'));
    };

    const body = (v) => {
        const [cls, a, b] = String(v.pair || '').split(':');
        return { vpc_id: vpc.id, peering_id: v.peering_id || 0, class: cls, a_id: a, b_id: b, mbps: v.mbps };
    };

    const onValues = async (changed, v) => {
        if ('peering_id' in changed) {
            form.setFieldsValue({ pair: undefined });
            loadPairs(changed.peering_id);
            setQuote(null);
            return;
        }
        if (!v.pair || !v.mbps) { setQuote(null); return; }
        const res = await call('get', '/api/user/evm/vpc-bw/quote', body(v));
        setQuote(res.status === 'success' ? res.data : { error: res.msg });
    };

    const buy = async (v) => {
        setSaving(true);
        const res = await call('post', '/api/user/evm/vpc-bw', body(v));
        setSaving(false);
        if (res.status !== 'success') {
            message.error(res.msg || t('Purchase failed'));
            return;
        }
        setOpen(false);
        if (res.data?.paid) {
            message.success(t('Active'));
        } else if (res.data?.invoice_id) {
            message.info(t('Insufficient balance. Please pay the invoice'));
            window.location.assign(`/billing/invoices/${res.data.invoice_id}`);
        }
        load();
    };

    const toggleRenew = async (c) => {
        const res = await call('post', `/api/user/evm/vpc-bw/${c.id}/auto-renew`, { auto_renew: c.auto_renew ? 0 : 1 });
        if (res.status === 'success') load();
    };

    if (!data) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    const pairOptions = (pairs || []).map((p) => ({ value: p.id, label: `${classLabel(p.class)} · ${p.a_name} ↔ ${p.b_name}` }));

    return (
        <div>
            <Space size={32} style={{ marginBottom: 8 }} wrap>
                <Statistic title={t('Cross-zone traffic this month')} value={fmtBytes(data.total_bytes)} />
                <Statistic title={t('Charges this month')} prefix={currencySymbol(data.currency || PLATFORM_CURRENCY)} value={data.total_amount} precision={2} />
                <Button size="small" icon={<PlusOutlined />} onClick={openBuy}>{t('Committed bandwidth')}</Button>
            </Space>
            <Table size="small" rowKey={(r) => r.class} pagination={false} dataSource={data.by_class}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No cross-zone traffic this month')} /> }}
                columns={[
                    { title: t('Type'), dataIndex: 'class', render: classLabel },
                    { title: t('Traffic'), dataIndex: 'bytes', render: fmtBytes },
                    { title: t('Peak'), dataIndex: 'peak_bps', render: (v) => `${(v / 1e6).toFixed(1)} Mbps` },
                    { title: t('Cost'), dataIndex: 'amount', render: (v) => formatMoney(v, data.currency || PLATFORM_CURRENCY) },
                ]} />
            {data.commits?.length > 0 && (
                <Table size="small" rowKey="id" pagination={false} style={{ marginTop: 8 }} dataSource={data.commits}
                    columns={[
                        { title: t('Committed bandwidth'), render: (_, c) => `${c.mbps} Mbps${c.peering_id ? ` · #${c.peering_id}` : ''}` },
                        {
                            title: t('Type'),
                            dataIndex: 'scope',
                            render: (s, c) => (
                                <>
                                    {classLabel(s === 'area' ? 'cross_region' : 'cross_az')}
                                    {c.matches === false && <Tooltip title={t('No instances on this pair any more; the commit will not be used')}><Tag color="orange" style={{ marginLeft: 6 }}>{t('No matching instances')}</Tag></Tooltip>}
                                </>
                            ),
                        },
                        { title: t('Status'), dataIndex: 'status', render: (s) => <Tag color={s === 'active' ? 'green' : 'orange'}>{s === 'active' ? t('Taking effect') : t('Payment pending')}</Tag> },
                        { title: t('Expires'), dataIndex: 'expires_at' },
                        { title: t('Auto-renewal'), render: (_, c) => <Button size="small" type="link" onClick={() => toggleRenew(c)}>{c.auto_renew ? t('On') : t('Off')}</Button> },
                    ]} />
            )}
            <Modal title={t('Buy committed bandwidth')} open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} confirmLoading={saving}
                okButtonProps={{ disabled: !quote || !!quote.error }} destroyOnClose>
                <Form form={form} layout="vertical" onFinish={buy} onValuesChange={onValues} initialValues={{ mbps: 10 }}>
                    {peerings.length > 0 && (
                        <Form.Item name="peering_id" label={t('Peering')} tooltip={t("Leave empty to apply to this VPC's own traffic")}>
                            <Select allowClear options={peerings.map((p) => ({ value: p.id, label: `#${p.id}` }))} />
                        </Form.Item>
                    )}
                    <Form.Item name="pair" label={t('Route')} tooltip={t('Only zone/region pairs with your instances')} rules={[{ required: true, message: t('Required') }]}>
                        <Select loading={pairs === null} options={pairOptions} notFoundContent={pairs ? t('No cross-zone instances') : null} />
                    </Form.Item>
                    <Form.Item name="mbps" label="Mbps" rules={[{ required: true, message: t('Required') }]}>
                        <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                    </Form.Item>
                    {quote && (quote.error
                        ? <div style={{ color: '#cf1322' }}>{quote.error}</div>
                        : <div>{t('{{amount}} / 30 days, overage billed per GB', { amount: formatMoney(quote.amount, quote.currency || PLATFORM_CURRENCY) })}</div>)}
                </Form>
            </Modal>
        </div>
    );
};
